/**
 * CreateProviderInstanceDialog - Modal para criar nova instância de provider
 * 
 * Permite ao admin criar solicitação de instância para um tenant,
 * tipicamente quando feito manualmente (não via Stripe).
 */

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus } from "lucide-react";
import { useProviderInstancesAdmin } from "@/hooks/useProviderInstances";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";

interface CreateProviderInstanceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateProviderInstanceDialog({
  open,
  onOpenChange,
}: CreateProviderInstanceDialogProps) {
  // Estados do form
  const [tenantId, setTenantId] = useState("");
  const [providerId, setProviderId] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("paid");
  const [notes, setNotes] = useState("");

  // Hook de mutações
  const { createInstance } = useProviderInstancesAdmin();

  // Buscar tenants
  const { data: tenants } = useQuery({
    queryKey: ['tenants-for-instance'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tenants')
        .select('id, name, slug')
        .eq('status', 'active')
        .order('name');
      if (error) throw error;
      return data;
    },
    enabled: open,
  });

  // Buscar providers disponíveis para o plano do tenant selecionado
  // Filtra pela matriz plan_providers e exclui webchat (não requer instância manual)
  const { data: providers } = useQuery({
    queryKey: ['providers-for-instance', tenantId],
    queryFn: async () => {
      // 1. Buscar plano do tenant selecionado
      const { data: tenant, error: tenantError } = await supabase
        .from('tenants')
        .select('plan')
        .eq('id', tenantId)
        .single();
      
      if (tenantError || !tenant) throw tenantError || new Error('Tenant não encontrado');
      
      // 2. Buscar providers disponíveis para esse plano via plan_providers
      const { data, error } = await supabase
        .from('plan_providers')
        .select(`
          provider_id,
          api_providers!inner (
            id, name, display_name, slug
          )
        `)
        .eq('plan_id', tenant.plan)
        .eq('is_available', true);
      
      if (error) throw error;
      
      // 3. Filtrar: excluir webchat (não requer instância manual) e retornar providers
      return data
        ?.filter(pp => (pp.api_providers as any)?.slug !== 'webchat')
        .map(pp => pp.api_providers as { id: string; name: string; display_name: string | null; slug: string });
    },
    enabled: open && !!tenantId, // Só executa quando tenant selecionado
  });

  // Reset form quando fechar
  useEffect(() => {
    if (!open) {
      setTenantId("");
      setProviderId("");
      setPaymentStatus("paid");
      setNotes("");
    }
  }, [open]);

  // Reset provider quando tenant mudar (provider pode não estar disponível no novo plano)
  useEffect(() => {
    setProviderId("");
  }, [tenantId]);

  // Handler de criação
  const handleCreate = async () => {
    if (!tenantId || !providerId) return;
    
    await createInstance.mutateAsync({
      tenant_id: tenantId,
      provider_id: providerId,
      source_type: 'manual',
      payment_status: paymentStatus as 'pending' | 'paid' | 'free_trial' | 'courtesy',
      notes: notes || undefined,
    });
    
    onOpenChange(false);
  };

  const isLoading = createInstance.isPending;
  const canCreate = tenantId && providerId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>Nova Instância</DialogTitle>
          <DialogDescription>
            Criar solicitação de instância para um tenant
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Tenant */}
          <div className="space-y-2">
            <Label>Tenant *</Label>
            <Select value={tenantId} onValueChange={setTenantId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione o tenant" />
              </SelectTrigger>
              <SelectContent>
                {tenants?.map((tenant) => (
                  <SelectItem key={tenant.id} value={tenant.id}>
                    {tenant.name} ({tenant.slug})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Provider */}
          <div className="space-y-2">
            <Label>Provider *</Label>
            <Select value={providerId} onValueChange={setProviderId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione o provider" />
              </SelectTrigger>
              <SelectContent>
                {providers?.map((provider) => (
                  <SelectItem key={provider.id} value={provider.id}>
                    {provider.display_name || provider.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Status de Pagamento */}
          <div className="space-y-2">
            <Label>Status de Pagamento</Label>
            <Select value={paymentStatus} onValueChange={setPaymentStatus}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="paid">Pago</SelectItem>
                <SelectItem value="pending">Pendente</SelectItem>
                <SelectItem value="free_trial">Free 7 dias</SelectItem>
                <SelectItem value="courtesy">Cortesia</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Notas */}
          <div className="space-y-2">
            <Label>Notas (opcional)</Label>
            <Textarea
              placeholder="Observações sobre esta instância..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleCreate} disabled={isLoading || !canCreate} className="gap-2">
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            Criar Instância
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
